import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Star, Clock, ArrowLeft, Coffee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopDetail = () => {
  const { id } = useParams();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchShop();
  }, [id]);

  const fetchShop = async () => {
    try {
      const response = await axios.get(`${API}/shops/${id}`);
      setShop(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shop:', error);
      setError('Coffee shop not found');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grain-overlay">
        <div className="px-6 md:px-12 lg:px-24 py-12">
          <div className="animate-pulse">
            <div className="bg-muted/50 h-96 rounded-xl mb-8" />
            <div className="bg-muted/50 h-12 w-1/2 rounded mb-4" />
            <div className="bg-muted/50 h-6 w-1/4 rounded mb-4" />
            <div className="bg-muted/50 h-24 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="min-h-screen grain-overlay flex items-center justify-center">
        <div className="text-center">
          <Coffee className="h-16 w-16 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="text-3xl font-semibold mb-2" data-testid="error-message">{error || 'Coffee shop not found'}</h2>
          <Link to="/directory">
            <Button data-testid="back-to-directory-button" className="mt-4" variant="outline">
              Back to Directory
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-overlay">
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={shop.image_url}
          alt={shop.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/80" />
        <Link to="/directory">
          <Button
            data-testid="back-button"
            variant="ghost"
            className="absolute top-6 left-6 md:left-12 gap-2 bg-white/90 backdrop-blur-sm hover:bg-white"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back
          </Button>
        </Link>
      </div>

      <div className="px-6 md:px-12 lg:px-24 -mt-20 relative z-10 pb-20">
        <Card className="bg-card shadow-lg border-border">
          <CardContent className="p-8 md:p-12">
            <div className="mb-8">
              <h1 className="text-5xl md:text-6xl font-semibold tracking-tight mb-4" data-testid="shop-detail-name">
                {shop.name}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" strokeWidth={1.5} />
                  <span className="text-base" data-testid="shop-detail-address">{shop.address}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 fill-accent text-accent" strokeWidth={1.5} />
                  <span className="text-base font-medium" data-testid="shop-detail-rating">{shop.rating.toFixed(1)} Rating</span>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-medium mb-4">About</h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed" data-testid="shop-detail-description">
                  {shop.description}
                </p>
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-medium mb-4">Details</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
                    <div>
                      <p className="font-medium mb-1">Hours</p>
                      <p className="text-muted-foreground" data-testid="shop-detail-hours">{shop.hours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Coffee className="h-5 w-5 text-accent mt-1" strokeWidth={1.5} />
                    <div>
                      <p className="font-medium mb-1">Specialties</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {shop.specialties.map((specialty, idx) => (
                          <span
                            key={idx}
                            data-testid={`specialty-${idx}`}
                            className="text-sm px-3 py-1 bg-secondary text-secondary-foreground rounded-full tracking-wide"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-8">
              <h2 className="text-2xl md:text-3xl font-medium mb-6">Location</h2>
              <div className="bg-muted/30 rounded-lg p-6 text-center">
                <MapPin className="h-12 w-12 text-accent mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-muted-foreground">
                  Latitude: {shop.latitude.toFixed(6)}, Longitude: {shop.longitude.toFixed(6)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ShopDetail;